import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import axiosInstance from "../../../utils/axiosInstance";
import "./ServiceDashboard.css";

const emptyEditForm = {
  id: "",
  serviceName: "",
  amount: "",
  startDate: "",
  duration: 1,
  durationUnit: "months",
  renewalType: "monthly",
  reminderDaysBefore: 3,
};

function Service() {
  const [services, setServices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [subscriptionError, setSubscriptionError] = useState("");
  const [editingSubscription, setEditingSubscription] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const navigate = useNavigate();

  const fetchServices = () => {
    axios
      .get(`${API_URL}/api/getServices`)
      .then((res) => {
        setServices(Array.isArray(res.data) ? res.data : []);
        setError("");
      })
      .catch(() => {
        setServices([]);
        setError("Unable to load services from the backend.");
      });
  };

  const fetchSubscriptions = () => {
    axios
      .get(`${API_URL}/api/serviceSubscriptions`)
      .then((res) => {
        setSubscriptions(Array.isArray(res.data) ? res.data : []);
        setSubscriptionError("");
      })
      .catch(() => {
        setSubscriptions([]);
        setSubscriptionError("Unable to load saved subscriptions.");
      });
  };

  useEffect(() => {
    fetchServices();
    fetchSubscriptions();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Delete service?")) {
      try {
        await axiosInstance.delete(`${API_URL}/api/deleteService/${id}`);
        fetchServices();
        fetchSubscriptions();
      } catch {
        alert("Delete failed");
      }
    }
  };

  const handleUpdate = (id) => {
    navigate(`/admin/updateService/${id}`);
  };

  const filteredServices = services.filter((service) =>
    service.serviceName.toLowerCase().includes(search.toLowerCase())
  );

  const groupedServices = filteredServices.reduce((acc, service) => {
    const dept = service.deptId?.deptName || "Unknown";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(service);
    return acc;
  }, {});

  const subscriptionByServiceId = useMemo(() => {
    const map = {};
    subscriptions.forEach((item) => {
      const serviceId = item.service_id?._id || item.service_id;
      if (serviceId) map[serviceId] = item;
    });
    return map;
  }, [subscriptions]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN");
  };

  const resetEditForm = () => {
    setEditForm(emptyEditForm);
    setEditingSubscription(false);
  };

  const openEditSubscriptionModal = (item) => {
    const startDate = item?.start_date
      ? new Date(item.start_date).toISOString().split("T")[0]
      : "";
    setEditForm({
      id: item._id,
      serviceName: item.service_name || item.service_id?.serviceName || "",
      amount: String(item.amount ?? ""),
      startDate,
      duration: item.duration_value || 1,
      durationUnit: item.renewal_type === "yearly" ? "years" : "months",
      renewalType: item.renewal_type || "monthly",
      reminderDaysBefore: Number.isInteger(item.reminder_days_before)
        ? item.reminder_days_before
        : 3,
    });
    setEditingSubscription(true);
  };

  const handleEditInput = (field, value) => {
    if (field === "durationUnit") {
      setEditForm((prev) => ({
        ...prev,
        durationUnit: value,
        renewalType: value === "years" ? "yearly" : "monthly",
      }));
      return;
    }

    if (field === "renewalType") {
      setEditForm((prev) => ({
        ...prev,
        renewalType: value,
        durationUnit: value === "yearly" ? "years" : "months",
      }));
      return;
    }

    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateEditForm = () => {
    if (!String(editForm.serviceName || "").trim()) return "Service name is required";
    if (!editForm.amount || Number(editForm.amount) <= 0) return "Amount must be a positive number";
    if (!editForm.startDate) return "Start date is required";
    if (!editForm.duration || Number(editForm.duration) <= 0) return "Duration must be a positive number";
    if (!editForm.renewalType) return "Renewal type is required";
    if (Number(editForm.reminderDaysBefore) < 0) return "Reminder days cannot be negative";
    return "";
  };

  const handleSaveSubscription = async (e) => {
    e.preventDefault();
    const validationMessage = validateEditForm();
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    try {
      await axiosInstance.put(`${API_URL}/api/serviceSubscriptions/${editForm.id}`, {
        serviceName: editForm.serviceName,
        amount: Number(editForm.amount),
        startDate: editForm.startDate,
        duration: Number(editForm.duration),
        renewalType: editForm.renewalType,
        reminderDaysBefore: Number(editForm.reminderDaysBefore),
      });
      resetEditForm();
      fetchServices();
      fetchSubscriptions();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update subscription");
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm("Delete this subscription?")) return;
    try {
      await axiosInstance.delete(`${API_URL}/api/serviceSubscriptions/${id}`);
      fetchServices();
      fetchSubscriptions();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete subscription");
    }
  };

  const handleSendTestReminder = async (id) => {
    try {
      await axiosInstance.post(`${API_URL}/api/serviceSubscriptions/${id}/test-reminder`);
      alert("Test reminder sent.");
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : "") ||
        err?.message;
      alert(backendMessage || "Failed to send test reminder");
    }
  };

  return (
    <div className="service-dashboard-page">
      <div className="service-dashboard-container">
        <div className="service-kpi-grid">
          <div className="service-kpi-card service-kpi-card-total">
            Total Services <br />
            <b>{services.length}</b>
          </div>
          <div className="service-kpi-card service-kpi-card-dept">
            Departments <br />
            <b>{Object.keys(groupedServices).length}</b>
          </div>
        </div>

        <div className="service-main-card">
          <div className="service-main-header">
            <h3 className="service-main-title">Services by Department</h3>
            <div className="service-header-actions">
              <Link to="/admin/addService" className="service-add-btn">
                Add
              </Link>
            </div>
          </div>

          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="service-search-box"
          />

          {error && <div className="service-error-banner">{error}</div>}

          {Object.keys(groupedServices).length > 0 ? (
            Object.keys(groupedServices).map((dept, index) => (
              <div key={dept} className="service-dept-card">
                <div
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${index}`}
                  className="service-dept-header"
                >
                  <span>{dept}</span>
                  <span className="service-dept-badge">{groupedServices[dept].length}</span>
                </div>

                <div
                  id={`collapse-${index}`}
                  className={`collapse ${index === 0 ? "show" : ""}`}
                >
                  <div className="service-dept-body">
                    <div className="service-card-grid">
                      {groupedServices[dept].map((service) => {
                        const subscription = subscriptionByServiceId[service._id];
                        return (
                          <div key={service._id} className="service-item-card">
                            <h6 className="service-item-title">{service.serviceName}</h6>
                            <p className="service-item-id">ID: {service.serviceId}</p>
                            <div className="service-price-pill">Rs. {service.servicePrice}</div>
                            {subscription ? (
                              <div className="service-subscription-pill">
                                Renewal: {subscription.renewal_type} | Next: {formatDate(subscription.next_billing_date)}
                              </div>
                            ) : null}
                            <div className="service-item-actions">
                              <button
                                onClick={() => handleUpdate(service._id)}
                                className="service-edit-btn"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(service._id)}
                                className="service-delete-btn"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="service-empty-state">No Services Found</div>
          )}
        </div>

        <div className="service-main-card service-subscription-card">
          <div className="service-main-header">
            <h3 className="service-main-title">Saved Subscriptions</h3>
          </div>

          {subscriptionError && <div className="service-error-banner">{subscriptionError}</div>}

          <div className="service-subscription-table-wrap">
            <table className="service-subscription-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Start Date</th>
                  <th>Duration</th>
                  <th>Renewal Type</th>
                  <th>Next Renewal</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="service-table-empty">
                      No saved subscriptions yet.
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((item) => (
                    <tr key={item._id}>
                      <td>{item.service_name || item.service_id?.serviceName || "-"}</td>
                      <td>Rs. {item.amount}</td>
                      <td>{formatDate(item.start_date)}</td>
                      <td>{item.duration_value}</td>
                      <td>{item.renewal_type}</td>
                      <td>{formatDate(item.next_billing_date)}</td>
                      <td>
                        <div className="service-item-actions">
                          <button
                            type="button"
                            className="service-delete-btn"
                            onClick={() => handleDeleteSubscription(item._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editingSubscription && (
        <div className="service-modal-overlay">
          <form className="service-modal" onSubmit={handleSaveSubscription}>
            <h4>Edit Subscription</h4>
            <div className="service-modal-grid">
              <div>
                <label>Service Name</label>
                <input
                  type="text"
                  value={editForm.serviceName}
                  onChange={(e) => handleEditInput("serviceName", e.target.value)}
                />
              </div>
              <div>
                <label>Amount</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => handleEditInput("amount", e.target.value)}
                />
              </div>
              <div>
                <label>Start Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => handleEditInput("startDate", e.target.value)}
                />
              </div>
              <div>
                <label>Duration</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "8px" }}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={editForm.duration}
                    onChange={(e) => handleEditInput("duration", e.target.value)}
                  />
                  <select
                    value={editForm.durationUnit}
                    onChange={(e) => handleEditInput("durationUnit", e.target.value)}
                  >
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
              </div>
              <div>
                <label>Renewal Type</label>
                <select
                  value={editForm.renewalType}
                  onChange={(e) => handleEditInput("renewalType", e.target.value)}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half-yearly">Half-yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label>Reminder Days Before</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editForm.reminderDaysBefore}
                  onChange={(e) => handleEditInput("reminderDaysBefore", e.target.value)}
                />
              </div>
            </div>
            <div className="service-modal-actions">
              <button type="button" className="service-edit-btn" onClick={resetEditForm}>
                Cancel
              </button>
              <button type="submit" className="service-delete-btn">
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Service;
